import type { QueryClient } from "@tanstack/react-query";
import { queryOptions, skipToken, useQuery } from "@tanstack/react-query";
import api from "../api";
import urls from "../urls";
import type { Person } from "../utils/backend-types";

export const personQueries = {
  list: (params: { variantId?: number; siteId?: number }) =>
    queryOptions({
      queryKey: ["persons", params]
    }),
  detail: (personId?: number) =>
    queryOptions({
      queryKey: ["persons", { personId }],
      queryFn:
        typeof personId !== "undefined"
          ? () => fetchPerson(personId)
          : skipToken,
      enabled: typeof personId !== "undefined",
      refetchInterval: false,
      refetchOnWindowFocus: false,
      ...queryOptions
    })
};

function usePerson(id?: number) {
  return useQuery(personQueries.detail(id));
}

async function fetchPerson(id: number) {
  const response = await api.get<Person>(urls.api.person(id));
  return response.data;
}

/**
 * Appends `person` to the cached `personQueries.list(params)` result so a
 * newly created/imported person shows up immediately, with no blank-select
 * flash while the list refetches. Skips an uncached list, which would
 * otherwise render as a one-entry select for a frame.
 */
function mergePersonIntoListCache(
  queryClient: QueryClient,
  params: { variantId?: number; siteId?: number },
  person: Person
) {
  queryClient.setQueryData(
    personQueries.list(params).queryKey,
    (old: Array<Person> | undefined) => (old ? [...old, person] : old)
  );
}

export { mergePersonIntoListCache, usePerson };
